import { FunctionComponent, useCallback } from "react";
import SvgFragment from "../../../../fragments/SvgFragment";
import Text from "../../../../components/Text";
import EmptyPage from "../../../../components/EmptyPage";
import Column from "../../../../components/Column";
import styled from "styled-components";
import { styles } from "../../../../styles/themes/style";
import {
    ChromeOnboardingIcon,
    LeadDeltaOnboardingIcon,
    OnboardingConnectionIconRow,
    OnboardingLinkIcon,
} from "../../../onboarding/components/OnboardingConnectionIcons";
import Button from "../../../../components/Button";
import { useExternalHandlers } from "../../../../services/actions/navigation/useExternalHandlers";
import { useAppStore } from "../../../../stores/application/useAppStore";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { useAuthHandlers } from "../../../../services/actions/auth/useAuthHandlers";
import { useInterval } from "../../../../wrappers/IntervalContext";
import { Progress } from 'antd';
import { triggerManualLinkedInSync } from "../../../onboarding/linkedinConnectFlowUtils";
import { isChromeBasedBrowser } from "../../../../utils";
import { getBrowserDisplayName } from "../../../onboarding/onboardingBrowserUtils";

interface IConnectionTableEmptyPageProps {
    props: 'leadsearch' | 'noExtensionInstalled' | 'unsupportedBrowser' | 'workspaceNotSynced' | 'syncConnectionsInProgress' | 'syncConnectionsDone' | 'default'
}

const ConnectionTableEmptyPage: FunctionComponent<IConnectionTableEmptyPageProps> = ({ props }) => {
    const { visitChromeStore, visitChromeBrowserDownload } = useExternalHandlers()
    const { store: { extensionInstalled, syncCompletionPercentage, onboardingStep } } = useAppStore()
    const { store: { user, workspace, authData } } = useAuthStore()

    const { setSyncConnectionsModalHandler } = useAppHandlers()
    const { syncConnectionsHandler, getUserHandler, getMeHandler, syncConversationsHandler } = useAuthHandlers()
    const { startInterval } = useInterval();
    
    const noResultTitle = "No Contacts To Show";
    const noResultDescription = "Install Chrome Extension, Sync Contacts, or wait for the Sync to complete. You can also import Contacts via CSV import.";

    const setLastSyncAt = useCallback(() => {
        if (!extensionInstalled || !user) return

        const runSync = () => {
            syncConnectionsHandler(user, false, authData)
            syncConversationsHandler()
            if (!user?.isSynced) {
                getMeHandler()
                startInterval(async () => getUserHandler(), 300000)
            }
        }

        triggerManualLinkedInSync({
            user,
            extensionInstalled,
            onboardingStep,
            onSync: runSync,
            onLegacySyncModal: () => setSyncConnectionsModalHandler(() => runSync()),
        })
    }, [
        user,
        extensionInstalled,
        authData,
        onboardingStep,
        getMeHandler,
        getUserHandler,
        setSyncConnectionsModalHandler,
        startInterval,
        syncConnectionsHandler,
        syncConversationsHandler,
    ])

    const leadsearch = <Column gap="24px" alignItems>
        <SvgFragment type="searchEmpty" />
        <StyledColumn width="460px" gap="8px" alignItems>
            <Text $heading3>Discover Top Prospects</Text>
            <Text $lighter $paragraph4>Search for profiles by city, job title, company name, industry, etc.</Text>
        </StyledColumn>
        <Button $type="blue" $bigButton tooltipMessage='' onClickHandler={() => window.open('https://www.loom.com/share/28e7d345ea2a49609d857c6cae0d1e6a?sid=e748dfd7-71fe-4d7d-8906-fd6391fa65ee', '_blank')}>Watch demo video</Button>
    </Column>

    const noExtensionInstalled = isChromeBasedBrowser() ? (
    <SyncPromptContainer>
        <SyncPromptContent>
            <OnboardingConnectionIconRow>
                <LeadDeltaOnboardingIcon aria-hidden />
                <OnboardingLinkIcon />
                <ChromeOnboardingIcon aria-hidden />
            </OnboardingConnectionIconRow>
            <SyncPromptTextBlock>
                <SyncPromptTitle>Install LeadDelta Chrome App</SyncPromptTitle>
                <SyncPromptDescription>
                    Sync your LinkedIn connections continuously, manage your inbox and a lot more.
                </SyncPromptDescription>
            </SyncPromptTextBlock>
        </SyncPromptContent>
        <SyncPromptButton type="button" onClick={() => {
            try { sessionStorage.setItem('ld_extension_install_source', 'contacts_empty_state') } catch {}
            visitChromeStore()
        }}>
            <SvgFragment type="openNewTab" />
            Install Chrome app
        </SyncPromptButton>
    </SyncPromptContainer>
    ) : null

    const unsupportedBrowser =
    <SyncPromptContainer>
        <SyncPromptContent>
            <SyncPromptTextBlock>
                <SyncPromptTitle>Use a Chromium-based browser</SyncPromptTitle>
                <SyncPromptDescription>
                    {`You’re using ${getBrowserDisplayName()}. Install Google Chrome to use the LeadDelta app and sync your LinkedIn contacts.`}
                </SyncPromptDescription>
            </SyncPromptTextBlock>
        </SyncPromptContent>
        <SyncPromptButton type="button" onClick={() => visitChromeBrowserDownload()}>
            <SvgFragment type="openNewTab" />
            Get Chrome
        </SyncPromptButton>
    </SyncPromptContainer>

    const workspaceNotSynced =
    <SyncPromptContainer>
        <SyncPromptContent>
            <SyncPromptIconCircle>
                <SvgFragment type="syncCircle" />
            </SyncPromptIconCircle>
            <SyncPromptTextBlock>
                <SyncPromptTitle>Sync your LinkedIn contacts</SyncPromptTitle>
                <SyncPromptDescription>
                    Sync your LinkedIn connections for the first time, manage your inbox and a lot more. After this initial set up LeadDelta will daily auto-sync. Set it and forget it.
                </SyncPromptDescription>
            </SyncPromptTextBlock>
        </SyncPromptContent>
        <SyncPromptButton type="button" onClick={() => setLastSyncAt()}>
            Sync contacts
        </SyncPromptButton>
    </SyncPromptContainer>

    const syncConnectionsInProgress = 
    <Column gap="24px" alignItems>
        <SyncProgressWrap>
            <Progress percent={syncCompletionPercentage} size="small" />
        </SyncProgressWrap>
        <StyledColumn gap="8px" alignItems>
            <Text $heading3>Syncing contacts</Text>
            <Text $lighter $paragraph4>Please hold on while we complete the sync of your LinkedIn contacts. This may take a few moments.</Text>
        </StyledColumn>
    </Column>

    const syncConnectionsDone = 
    <Column gap="24px" alignItems>
        <Oval green><SvgFragment type="checkmarkBig" /></Oval>
        <StyledColumn gap="8px" alignItems>
            <Text $heading3>Syncing contacts finished</Text>
            <Text $lighter $paragraph4>You can now refresh your page to see the contacts.</Text>
        </StyledColumn>
        <Button $type="blue" $bigButton tooltipMessage='' onClickHandler={() => window.location.reload()}>Refresh Your Page</Button>
    </Column>

    const contentMap = {
        leadsearch,
        noExtensionInstalled,
        unsupportedBrowser,
        workspaceNotSynced,
        syncConnectionsInProgress,
        syncConnectionsDone,
        default: <EmptyPage svgType='templateEmpty' title={noResultTitle} label={noResultDescription} />
    };

    return <EmptyStatePageWrapper>{contentMap[props]}</EmptyStatePageWrapper>;
};

const EmptyStatePageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100%;
    width: 100%;
    box-sizing: border-box;
`
const StyledColumn = styled(Column)<{width?: string}>`
    width: ${({ width }) => width || '360px'};
    text-align: center;
`
const Oval = styled.div<{ green?: boolean }>`
    padding: 8px;
    background-color: #FFF;
    filter: drop-shadow(0px 0px 12px rgba(51, 57, 81, 0.10));
    border-radius: 50%;

    ${({ green }) => green && `
        & svg path { fill: ${styles.colors.green600} }
    `}
`

const SyncPromptContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
    width: 100%;
`

const SyncPromptContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    width: 360px;
    max-width: 100%;
`

const SyncProgressWrap = styled.div`
    width: 360px;
    max-width: 100%;

    .ant-progress {
        width: 100%;
    }
`

const SyncPromptIconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
`

const SyncPromptTextBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
`

const SyncPromptTitle = styled.h3`
    margin: 0;
    width: 100%;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    text-align: center;
    color: ${styles.colors.black600};
`

const SyncPromptDescription = styled.p`
    margin: 0;
    width: 100%;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: ${styles.colors.black400};
`

const SyncPromptButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 5px 16px;
    min-width: 122px;
    height: 52px;
    border: 1px solid ${styles.colors.primary600};
    border-radius: 12px;
    background: ${styles.colors.primary600};
    color: ${styles.colors.white};
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    & svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
    }

    &:hover {
        background: ${styles.colors.primary400};
        border-color: ${styles.colors.primary400};
    }

    &:focus {
        outline: none;
    }
`

export default ConnectionTableEmptyPage